import { Menu } from "lucide-react"
import type { ReactNode } from "react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useHotkeys } from "../hooks/useHotkeys"
import { nudgeColor } from "../hooks/useNudgeTarget"
import { CommandPalette } from "./CommandPalette"
import { Navigation } from "./Navigation"

const G_CHORD_MAP: Record<string, string> = {
	"?": "/help",
	a: "/accessibility",
	b: "/blend-modes",
	c: "/convert",
	d: "/data-viz",
	e: "/analog-effects",
	f: "/color-diff",
	h: "/",
	i: "/interpolation",
	j: "/adjustments",
	l: "/color-spaces",
	m: "/alpha-mixing",
	o: "/color-correction",
	p: "/palettes",
	s: "/standards",
	u: "/gamut-mapping",
	v: "/color-blindness",
	w: "/harmony",
	x: "/image-extraction",
	y: "/css-playground",
}

export function Layout({ children }: { children: ReactNode }) {
	const [sidebarOpen, setSidebarOpen] = useState(false)
	const [collapsed, setCollapsed] = useState(false)
	const [gPending, setGPending] = useState(false)
	const navigate = useNavigate()
	const gTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	const toggleCollapse = useCallback(() => setCollapsed((c) => !c), [])

	useHotkeys(
		useMemo(
			() => ({
				"?": (e) => {
					e.preventDefault()
					navigate("/help")
				},
				"[": (e) => {
					e.preventDefault()
					toggleCollapse()
				},
				ArrowDown: (e) => {
					if (!e.shiftKey) return
					e.preventDefault()
					nudgeColor("ArrowDown")
				},
				ArrowLeft: (e) => {
					if (!e.shiftKey) return
					e.preventDefault()
					nudgeColor("ArrowLeft")
				},
				ArrowRight: (e) => {
					if (!e.shiftKey) return
					e.preventDefault()
					nudgeColor("ArrowRight")
				},
				ArrowUp: (e) => {
					if (!e.shiftKey) return
					e.preventDefault()
					nudgeColor("ArrowUp")
				},
			}),
			[navigate, toggleCollapse],
		),
	)

	// G-chord navigation (capture phase so we can intercept before page handlers)
	useEffect(() => {
		let pending = false

		function handleKeyDown(e: KeyboardEvent) {
			if (e.ctrlKey || e.metaKey || e.altKey) return

			const target = e.target as HTMLElement
			if (
				target instanceof HTMLInputElement ||
				target instanceof HTMLTextAreaElement ||
				target.isContentEditable
			) {
				return
			}

			if (!pending && (e.key === "g" || e.key === "G")) {
				pending = true
				setGPending(true)
				if (gTimeoutRef.current) clearTimeout(gTimeoutRef.current)
				gTimeoutRef.current = setTimeout(() => {
					pending = false
					setGPending(false)
				}, 800)
				return
			}

			if (pending) {
				const route = G_CHORD_MAP[e.key]
				if (route) {
					e.preventDefault()
					e.stopImmediatePropagation()
					navigate(route)
				}
				pending = false
				setGPending(false)
				if (gTimeoutRef.current) clearTimeout(gTimeoutRef.current)
			}
		}

		window.addEventListener("keydown", handleKeyDown, true)
		return () => {
			window.removeEventListener("keydown", handleKeyDown, true)
			if (gTimeoutRef.current) clearTimeout(gTimeoutRef.current)
		}
	}, [navigate])

	return (
		<div
			className="min-h-screen flex"
			style={{ backgroundColor: "var(--bg-page)" }}
		>
			<Navigation
				collapsed={collapsed}
				isOpen={sidebarOpen}
				onClose={() => setSidebarOpen(false)}
			/>
			<div className="flex-1 flex flex-col min-w-0">
				<header
					className="md:hidden sticky top-0 z-40 backdrop-blur border-b border-subtle px-4 h-14 flex items-center"
					style={{
						backgroundColor:
							"color-mix(in oklch, var(--bg-page) 90%, transparent)",
					}}
				>
					<button
						className="text-neutral-500 hover:text-neutral-300 p-1"
						onClick={() => setSidebarOpen(true)}
						type="button"
					>
						<Menu className="w-5 h-5" />
					</button>
					<a
						className="ml-3 font-semibold text-sm text-neutral-400 hover:opacity-80 transition-opacity"
						href="https://gitlab.com/open-kappa/nodejs/colors"
						rel="noopener noreferrer"
						target="_blank"
					>
						@open-kappa/colors
					</a>
				</header>
				<main className="flex-1 overflow-y-auto p-4 md:p-6">{children}</main>
			</div>
			<CommandPalette />
			{gPending && (
				<div className="fixed bottom-6 right-6 z-50 px-3 py-1.5 rounded-lg bg-neutral-800 border border-neutral-700 text-neutral-400 text-sm font-mono animate-pulse">
					g…
				</div>
			)}
		</div>
	)
}
